// /xp-metrics — XP-sources economy dashboard (staff-only).
// Epic #550 Phase 1, issue #566.
//
// Design note: logs__exp_players is the canonical unified XP source (it already
// aggregates XP by activity per player per day). We do NOT also sum
// gym_players/crime_players/career XP columns — that would double-count.
//
// Limitation note: logs__level records level-up events only; it does not store
// how much XP triggered the level-up. Level-up counts are event counts only.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { EconomyDashboardLayout } from '@/components/admin/economy-dashboard-layout';

// ── Types ─────────────────────────────────────────────────────────────────────

interface XpActivityRow {
  day: string;
  combat: number;
  gym: number;
  crime: number;
  career: number;
  jail: number;
  special: number;
}

interface LevelUpDayRow {
  day: string;
  count: number;
}

interface XpMetricsResponse {
  ok: boolean;
  range: { since: number | null; until: number | null };
  xp_by_activity: XpActivityRow[];
  level_ups_by_day: LevelUpDayRow[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildQueryString(since: string, until: string, userId: string): string {
  const params = new URLSearchParams();
  if (since.trim()) params.set('since', since.trim());
  if (until.trim()) params.set('until', until.trim());
  if (userId.trim()) params.set('userId', userId.trim());
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ── Component ─────────────────────────────────────────────────────────────────

export function XpMetricsPage() {
  const [draftSince, setDraftSince] = useState('');
  const [draftUntil, setDraftUntil] = useState('');
  const [draftUserId, setDraftUserId] = useState('');
  const [appliedSince, setAppliedSince] = useState('');
  const [appliedUntil, setAppliedUntil] = useState('');
  const [appliedUserId, setAppliedUserId] = useState('');

  const { data, isLoading, isError } = useQuery<XpMetricsResponse>({
    queryKey: ['xp-metrics', appliedSince, appliedUntil, appliedUserId],
    queryFn: async () => {
      const qs = buildQueryString(appliedSince, appliedUntil, appliedUserId);
      const res = await apiFetch<XpMetricsResponse>(`/admin/xp-metrics${qs}`);
      if (!res.ok) throw new Error('Failed to load XP metrics.');
      return res.data;
    },
  });

  function handleApply() {
    setAppliedSince(draftSince);
    setAppliedUntil(draftUntil);
    setAppliedUserId(draftUserId);
  }

  return (
    <EconomyDashboardLayout
      title="XP Sources"
      description="XP earned by activity type per day, plus level-up milestone counts. Defaults to all-time; narrow by Unix timestamp range."
      since={draftSince}
      until={draftUntil}
      onSinceChange={setDraftSince}
      onUntilChange={setDraftUntil}
      userId={draftUserId}
      onUserIdChange={setDraftUserId}
      onApply={handleApply}
      isLoading={isLoading}
      isError={isError}
      errorMessage="Failed to load XP metrics."
    >
      <p className="text-sm text-ink-mid italic">
        XP data sourced from <code className="font-mono text-xs">logs__exp_players</code> — the unified
        per-activity roll-up. Summing activity-specific tables (gym_players, crime_players, etc.) would
        double-count; this dashboard uses the canonical source only.
      </p>

      {data != null && (
        <>
          {/* XP by Activity */}
          <section className="space-y-2">
            <h2 className="text-lg font-medium text-ink-high">XP by Activity</h2>
            {data.xp_by_activity.length > 0 ? (
              <div className="overflow-x-auto rounded-sm border border-border">
                <table className="w-full text-sm" data-testid="xp-by-activity-table">
                  <thead className="bg-muted text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium text-ink-mid">Day</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Combat</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Gym</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Crime</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Career</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Jail</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Special</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-divider">
                    {data.xp_by_activity.map((row) => (
                      <tr key={row.day} className="hover:bg-panel-elev">
                        <td className="px-3 py-2 font-mono text-xs">{row.day}</td>
                        <td className="px-3 py-2 tabular-nums">{row.combat.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                        <td className="px-3 py-2 tabular-nums">{row.gym.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                        <td className="px-3 py-2 tabular-nums">{row.crime.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                        <td className="px-3 py-2 tabular-nums">{row.career.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                        <td className="px-3 py-2 tabular-nums">{row.jail.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                        <td className="px-3 py-2 tabular-nums">{row.special.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-ink-low">No XP activity data available.</p>
            )}
          </section>

          {/* Level-up Milestones */}
          <section className="space-y-2">
            <h2 className="text-lg font-medium text-ink-high">Level-up Milestones</h2>
            <p className="text-sm text-ink-mid">
              Event count only. <code className="font-mono text-xs">logs__level</code> records when a
              level-up occurred but does not store the XP amount that triggered it.
            </p>
            {data.level_ups_by_day.length > 0 ? (
              <div className="overflow-x-auto rounded-sm border border-border">
                <table className="w-full text-sm" data-testid="level-ups-by-day-table">
                  <thead className="bg-muted text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium text-ink-mid">Day</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Level-ups</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-divider">
                    {data.level_ups_by_day.map((row) => (
                      <tr key={row.day} className="hover:bg-panel-elev">
                        <td className="px-3 py-2 font-mono text-xs">{row.day}</td>
                        <td className="px-3 py-2 tabular-nums">{row.count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-ink-low">No level-up data available.</p>
            )}
          </section>
        </>
      )}
    </EconomyDashboardLayout>
  );
}
